/**
 * StreamingLayout -- Layout route wrapping all /streaming/dashboard/* pages.
 *
 * Provides the StreamingFlyoutContext so that Analytics, Logs, and Settings
 * flyout panels can open from any streaming subpage (Dashboard, Control Center,
 * Server Users, Devices) without navigating away.
 *
 * Listens for:
 * - URL hash changes (e.g. #analytics, #logs, #settings)
 * - Custom DOM events dispatched by SidebarNav for hash-link clicks
 */
import { lazy, Suspense, useCallback, useEffect, useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { SideFlyout } from "../ui/SideFlyout";
import {
  StreamingFlyoutProvider,
  useStreamingFlyout,
  type StreamingFlyoutMode,
} from "../../contexts/StreamingFlyoutContext";
import { MirrorDockProvider } from "../../contexts/MirrorDockContext";
import { CoverArtSettingsProvider } from "../../contexts/CoverArtSettingsContext";
import { useWSDSettings } from "../../hooks/useWSDSettings";
import { useWSDSessions } from "../../hooks/useWSDSessions";

// Lazy-load flyout panel content
const AnalyticsPanel = lazy(() =>
  import("./AnalyticsPanel").then((m) => ({ default: m.AnalyticsPanel }))
);
const WsdLogsPanel = lazy(() =>
  import("./WsdLogsPanel").then((m) => ({ default: m.WsdLogsPanel }))
);
const StreamingSettings = lazy(() =>
  import("./StreamingSettings").then((m) => ({ default: m.StreamingSettings }))
);
const MirrorPlayerDock = lazy(() =>
  import("./mirror/MirrorPlayerDock").then((m) => ({ default: m.MirrorPlayerDock }))
);

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FLYOUT_PANEL_ID = "wsd-flyout-panel";

const FLYOUT_TITLES: Record<Exclude<StreamingFlyoutMode, null>, string> = {
  analytics: "Watch Analytics",
  settings: "Streaming Settings",
  logs: "WSD Activity Logs",
};

const FLYOUT_ICONS: Record<Exclude<StreamingFlyoutMode, null>, string> = {
  analytics: "lucide:bar-chart-2",
  settings: "lucide:settings",
  logs: "lucide:scroll-text",
};

/** Hash fragments that redirect to Control Center subpages */
const HASH_REDIRECTS: Record<string, string> = {
  users: "/streaming/dashboard/control-center/server-users",
  devices: "/streaming/dashboard/control-center/devices",
};

/** Map hash → flyout mode */
const hashToFlyout = (hash: string): Exclude<StreamingFlyoutMode, null> | null => {
  const normalized = hash.replace(/^#/, "");
  if (normalized === "analytics" || normalized === "logs" || normalized === "settings") {
    return normalized;
  }
  return null;
};

// Custom event name used by SidebarNav to trigger flyouts
const FLYOUT_EVENT = "streaming-flyout-open";

// ---------------------------------------------------------------------------
// Inner layout (consumes context)
// ---------------------------------------------------------------------------

const StreamingLayoutInner = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { flyoutMode, openFlyout, closeFlyout } = useStreamingFlyout();

  // Settings flyout needs its own hooks
  const {
    settings,
    loading: settingsLoading,
    saving,
    testing,
    updateSettings,
    testConnection,
  } = useWSDSettings();

  const { sortedSessions, reconnect } = useWSDSessions();

  const activeTunerIds = useMemo(
    () =>
      sortedSessions
        .filter((s) => s.mediaDetails?.type === "Live TV" && s.mediaSourceId)
        .map((s) => s.mediaSourceId as string),
    [sortedSessions]
  );

  // Wrap updateSettings to also reconnect SSE after successful save
  const handleSettingsSave = useCallback(
    async (update: Parameters<typeof updateSettings>[0]) => {
      const success = await updateSettings(update);
      if (success) reconnect();
      return success;
    },
    [updateSettings, reconnect]
  );

  // -- Hash change handler (sidebar links or direct URL navigation)
  useEffect(() => {
    if (!location.hash) return;

    const normalized = location.hash.replace(/^#/, "");

    // Redirect users/devices hashes to Control Center subpages
    const redirect = HASH_REDIRECTS[normalized];
    if (redirect) {
      navigate(redirect, { replace: true });
      return;
    }

    const mode = hashToFlyout(location.hash);
    if (mode) {
      openFlyout(mode);
      // Clear the hash so re-clicking the same link triggers the effect again
      navigate(location.pathname, { replace: true });
    }
  }, [location.hash, location.pathname, navigate, openFlyout]);

  // -- Custom event handler (fired by SidebarNav when already on a streaming page)
  useEffect(() => {
    const handler = (e: Event) => {
      const mode = (e as CustomEvent<string>).detail;
      if (mode === "analytics" || mode === "logs" || mode === "settings") {
        openFlyout(mode);
      }
    };
    window.addEventListener(FLYOUT_EVENT, handler);
    return () => window.removeEventListener(FLYOUT_EVENT, handler);
  }, [openFlyout]);

  const handleFlyoutOpenChange = useCallback(
    (open: boolean) => {
      if (!open) closeFlyout();
    },
    [closeFlyout]
  );

  const flyoutTitle = flyoutMode ? FLYOUT_TITLES[flyoutMode] : "";
  const flyoutIcon = flyoutMode ? FLYOUT_ICONS[flyoutMode] : "";

  return (
    <>
      <Outlet />

      {/* Shared flyout panels (rendered as portal on document.body) */}
      <SideFlyout
        open={flyoutMode !== null}
        onOpenChange={handleFlyoutOpenChange}
        title={flyoutTitle}
        icon={flyoutIcon}
        panelId={FLYOUT_PANEL_ID}
      >
        <Suspense fallback={null}>
          {flyoutMode === "analytics" && (
            <div className="p-5">
              <AnalyticsPanel />
            </div>
          )}
          {flyoutMode === "settings" && (
            <div className="p-5">
              <StreamingSettings
                settings={settings}
                loading={settingsLoading}
                saving={saving}
                testing={testing}
                compact
                onSave={handleSettingsSave}
                onTestConnection={testConnection}
                activeTunerIds={activeTunerIds}
              />
            </div>
          )}
          {flyoutMode === "logs" && <WsdLogsPanel />}
        </Suspense>
      </SideFlyout>

      {/* Floating mirror player dock (portal to document.body) */}
      <Suspense fallback={null}>
        <MirrorPlayerDock />
      </Suspense>
    </>
  );
};

// ---------------------------------------------------------------------------
// Exported layout component (wraps with providers)
// ---------------------------------------------------------------------------

export const StreamingLayout = () => (
  <StreamingFlyoutProvider>
    <MirrorDockProvider>
      <CoverArtSettingsProvider>
        <StreamingLayoutInner />
      </CoverArtSettingsProvider>
    </MirrorDockProvider>
  </StreamingFlyoutProvider>
);
